window.UpsellUtils = Object.assign(window.UpsellUtils || {}, {
  normalizeSize(value) {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[“”″’’']/g, '"')
      .replace(/\s+/g, '')
      .trim();
  },

  matchUpsellVariant(parentTitle, variants = []) {
    if (!variants.length) return null;

    const normalizedParent = this.normalizeSize(parentTitle);
    if (!normalizedParent || normalizedParent === 'defaulttitle') {
      return variants.find((variant) => variant.available !== false) || variants[0];
    }

    const exact = variants.find((variant) => this.normalizeSize(variant.title) === normalizedParent);
    if (exact) return exact;

    const parentToken = normalizedParent.replace(/"/g, '');
    const tokenMatch = variants.find((variant) => {
      const token = this.normalizeSize(variant.title).replace(/"/g, '');
      if (token === parentToken) return true;
      if (!token.startsWith(parentToken) || token.length <= parentToken.length) return false;
      return !/\d/.test(token.charAt(parentToken.length));
    });
    if (tokenMatch) return tokenMatch;

    return null;
  },

  resolveUpsellForParent(parentTitle, recommendations = []) {
    if (!recommendations.length) return null;

    for (const recommendation of recommendations) {
      const variants = recommendation.variants || [];

      if (recommendation.directAssignment && variants.length) {
        const assigned = variants[0];
        return {
          ...assigned,
          product_title: recommendation.product_title || assigned.product_title,
        };
      }

      const matched = this.matchUpsellVariant(parentTitle, variants);
      if (matched) {
        return {
          ...matched,
          product_title: recommendation.product_title || matched.product_title,
        };
      }
    }

    return null;
  },

  getNestedParentKey(cartItem) {
    return cartItem?.parent_relationship?.parent_key || cartItem?.parent_relationship?.parent?.key || '';
  },

  findParentLineForVariant(cartState, variantId) {
    if (!cartState?.items?.length || !variantId) return null;

    const matches = cartState.items.filter(
      (item) => !this.getNestedParentKey(item) && Number(item.variant_id) === Number(variantId)
    );

    return matches[matches.length - 1] || null;
  },

  hasNestedUpsell(cartState, { parentLineKey, upsellVariantId }) {
    if (!cartState?.items?.length || !parentLineKey) return false;

    return cartState.items.some((item) => {
      if (this.getNestedParentKey(item) !== parentLineKey) return false;
      if (!upsellVariantId) return true;
      return Number(item.variant_id) === Number(upsellVariantId);
    });
  },

  findOrphanUpsellLines(cartState, upsellVariantId) {
    if (!cartState?.items?.length || !upsellVariantId) return [];

    return cartState.items.filter(
      (item) => Number(item.variant_id) === Number(upsellVariantId) && !this.getNestedParentKey(item)
    );
  },

  async fetchCartState() {
    return fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .catch(() => null);
  },

  async hydrateSections(cartState, sections) {
    if (!sections) return cartState;

    const response = await fetch(`${routes.cart_url}.js?sections=${sections}`, {
      headers: { Accept: 'application/json' },
    })
      .then((result) => (result.ok ? result.json() : null))
      .catch(() => null);

    if (!response?.sections) return cartState || response;

    return {
      ...(cartState || {}),
      ...response,
      sections: response.sections,
    };
  },

  async removeCartLine(lineKey) {
    const config = fetchConfig('javascript');
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify({ id: lineKey, quantity: 0 });

    const response = await fetch(`${routes.cart_change_url}`, config);
    return response.json();
  },

  async addNestedUpsellLine({ upsellVariantId, quantity, parentLineKey, sections, sectionsUrl }) {
    const config = fetchConfig('javascript');
    config.headers['Content-Type'] = 'application/json';

    const payload = {
      items: [
        {
          id: Number(upsellVariantId),
          quantity,
          parent_line_key: parentLineKey,
        },
      ],
    };

    if (sections) {
      payload.sections = sections;
      payload.sections_url = sectionsUrl || window.location.pathname;
    }

    config.body = JSON.stringify(payload);

    const response = await fetch(`${routes.cart_add_url}`, config);
    return response.json();
  },

  async ensureNestedUpsellAdded({ variantId, upsellVariantId, quantity, sections, sectionsUrl }) {
    let cartState = await this.fetchCartState();
    if (!cartState?.items) return null;

    const parentLine = this.findParentLineForVariant(cartState, variantId);
    if (!parentLine) return cartState;

    // Remove any standalone (non-nested) copies of this insert so checkout
    // doesn't show the insert as a separate product next to the cover.
    const orphans = this.findOrphanUpsellLines(cartState, upsellVariantId);
    for (const orphan of orphans) {
      await this.removeCartLine(orphan.key);
    }

    if (orphans.length) {
      cartState = await this.fetchCartState();
      if (!cartState?.items) return null;
    }

    const refreshedParent = this.findParentLineForVariant(cartState, variantId) || parentLine;

    if (this.hasNestedUpsell(cartState, { parentLineKey: refreshedParent.key, upsellVariantId })) {
      return this.hydrateSections(cartState, sections);
    }

    const nestedResponse = await this.addNestedUpsellLine({
      upsellVariantId,
      quantity,
      parentLineKey: refreshedParent.key,
      sections,
      sectionsUrl,
    });

    if (nestedResponse?.status) {
      console.warn('Failed to add nested upsell:', nestedResponse.description || nestedResponse.message);
      return nestedResponse;
    }

    cartState = await this.fetchCartState();
    if (!cartState) return nestedResponse;

    if (!this.hasNestedUpsell(cartState, { parentLineKey: refreshedParent.key, upsellVariantId })) {
      return {
        status: 422,
        message: 'Unable to nest insert under cover',
        description: 'Unable to nest insert under cover. Please try again.',
      };
    }

    return this.hydrateSections(cartState, sections);
  },
});
